import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import _ from 'lodash';

import Yup from '../../../utils/yup';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import useUsers from '../../../context/UsersContext';
import documentTypeService from '../../../service/newDocumentType.service';
import { FormHeaderUI, FormButtonPanelUI, FormBase } from '../../Interface';
import DocumentTypeFields from './DocumentTypeFields';
import DocumentTypeReports from './DocumentTypeReports';
import DocumentTypeTabs from './DocumentTypeTabs';
import masterDataService from '../../../service/masterData.service';
import { generateAutocompleteItems } from '../../../utils/helper';

const NewDocumentTypePage = () => {
  const { getUser, user } = useUsers();
  const [initValues, setInitValues] = useState({});
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autocompleteOptions, setAutocompleteOptions] = useState({
    items: [],
    warehouse: [],
    language: [],
  });

  const defaultValues = {
    documentTypeId: '',
    name: '',
    direction: '',
    defaultReplyToLevel: '',
    replyToAddress: '',
    inventoryAccounting: false,
    inventoryMovement: false,
    company: true,
    project: true,
    cooperation: false,
    employee: false,
    active: false,
    enableSettlement: false,
    useBankAccount: false,
    useDocumentPriority: false,
    editableItemName: false,
    defaultItemCode: '',
    defaultWarehouse: null,
    mobileTargetCustomStatus: null,
    additionalCost: false,
    invoice: false,
    invoiceConfirmation: false,
    manualDocNum: false,
    disableDatePrefill: false,
    printReport: [],
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();

    setInitValues(defaultValues);
    initDefaultValues();
  }, []);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (selectedValues) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const valuesToForm = () => {
    const {
      documentTypeId,
      name,
      direction,
      defaultReplyToLevel,
      replyToAddress,
      inventoryAccounting,
      inventoryMovement,
      company,
      project,
      cooperation,
      employee,
      active,
      enableSettlement,
      useBankAccount,
      useDocumentPriority,
      editableItemName,
      defaultItemCode,
      mobileTargetCustomStatus,
      additionalCost,
      invoice,
      invoiceConfirmation,
      manualDocNum,
      disableDatePrefill,
    } = selectedValues;

    setInitValues({
      documentTypeId,
      name,
      direction,
      defaultReplyToLevel: defaultReplyToLevel ?? '',
      replyToAddress: replyToAddress ?? '',
      inventoryAccounting,
      inventoryMovement,
      company,
      project,
      cooperation,
      employee,
      active,
      enableSettlement,
      useBankAccount,
      useDocumentPriority,
      editableItemName,
      defaultItemCode,
      defaultWarehouse: selectedValues.defaultWarehouse ?? null,
      mobileTargetCustomStatus: mobileTargetCustomStatus ?? null,
      additionalCost,
      invoice,
      invoiceConfirmation: Boolean(invoiceConfirmation),
      manualDocNum: Boolean(manualDocNum),
      disableDatePrefill: Boolean(disableDatePrefill),
      printReport: (selectedValues.printReport ?? []).map((row) => ({
        ...row,
        label: row.label ?? '',
        langCode: row.langCode ?? row.language?.code ?? null,
        isDraft: false,
      })),
    });
  };

  const initDefaultValues = () => {
    Promise.all([
      masterDataService.itemsFromTableToForm(),
      masterDataService.warehouseToForm(),
      masterDataService.languageToForm(),
    ])
      .then(([items, warehouse, language]) => {
        const aData = {
          items,
          warehouse,
          language,
        };

        const acOptions = {
          items: generateAutocompleteItems(aData.items, ['itemCode', 'itemName'], 'itemCode'),
          warehouse: generateAutocompleteItems(aData.warehouse, ['code', 'name'], 'code'),
          language: generateAutocompleteItems(aData.language, ['code', 'name'], 'code'),
        };
        setAutocompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const validationSchema = Yup.object().shape({
    documentTypeId: Yup.string().required(),
    name: Yup.string().required(),
    direction: Yup.string().required(),
    defaultReplyToLevel: Yup.string().nullable(),
    replyToAddress: Yup.string().email().nullable(),
    inventoryAccounting: Yup.boolean(),
    inventoryMovement: Yup.boolean(),
    company: Yup.boolean(),
    project: Yup.boolean(),
    cooperation: Yup.boolean(),
    employee: Yup.boolean(),
    active: Yup.boolean(),
    enableSettlement: Yup.boolean(),
    useBankAccount: Yup.boolean(),
    useDocumentPriority: Yup.boolean(),
    editableItemName: Yup.boolean(),
    defaultItemCode: Yup.string().nullable(),
    defaultWarehouse: Yup.string().nullable(),
    mobileTargetCustomStatus: Yup.string().nullable(),
    additionalCost: Yup.boolean(),
    invoice: Yup.boolean(),
    invoiceConfirmation: Yup.boolean(),
    manualDocNum: Yup.boolean(),
    disableDatePrefill: Yup.boolean(),
  });

  const updateObject = async (values) => {
    const updData = {
      ...values,
      updatedBy: user.userId,
      existingFiles: (values.printReport ?? []).filter((row) => !row.isDraft).map((row) => row.name),
      printReport: (values.printReport ?? []).map((row) => ({
        ...(row.isDraft ? {} : { id: row.id }),
        name: row.name,
        label: row.label ?? '',
        langCode: row.langCode,
        docDefault: Boolean(row.docDefault),
        docLangDefault: Boolean(row.docLangDefault),
        isNew: Boolean(row.isDraft),
      })),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(updData));

    for (const attachment of (values.printReport ?? []).filter((row) => row.isDraft)) {
      if (attachment.file instanceof File) {
        formData.append('files', attachment.file);
      }
    }

    return documentTypeService.updateDocumentType(formData, selectedValues.documentTypeId);
  };

  const handleSubmit = async (values) => {
    if (!editButtonVisible) {
      const invalidAttachment = (values.printReport ?? []).find((attachment) => !attachment.langCode);

      if (invalidAttachment) {
        addNotification({
          content: `A nyelv kiválasztása kötelező minden új mellékletnél: ${invalidAttachment.name}`,
          type: NotificationType.ERROR,
        });
        return;
      }

      try {
        const data = await updateObject({
          ...values,
        });

        addNotification({
          content: 'Sikeres módosítás!',
          type: NotificationType.SUCCESS,
        });
        if (data?.printReport) {
          setSelectedValues((prev) => ({ ...prev, ...data }));
        }
        setRefetchTable(true);
        setEditButtonvisible(true);
        setViewField(true);
      } catch (error) {
        addNotification({
          content: error?.message ?? 'A mentés nem sikerült.',
          type: NotificationType.ERROR,
        });
      }
    }
  };

  return (
    <>
      <FormBase
        name="DocumentType"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <DocumentTypeFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      autocompleteOptions={autocompleteOptions}
                    />

                    <DocumentTypeTabs
                      user={user}
                      selectedValues={selectedValues}
                      setSelectedValues={setSelectedValues}
                      disabled={viewField}
                      setRefetchTable={setRefetchTable}
                      languageOptions={autocompleteOptions.language}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);
                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <>
            <DocumentTypeReports
              refetch={{ refetchTable, setRefetchTable }}
              values={{ selectedValues, setSelectedValues }}
              viewField={viewField}
              operatorsDisabled={operatorsDisabled}
              rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            />
          </>
        }
      />
    </>
  );
};

export default NewDocumentTypePage;
